/**
 * TradeRadar — /league/:id/trade/radar (Trade Analyser split, Job 1)
 *
 * Scans every other manager's roster against yours and ranks trades worth
 * pitching. Only fetches the radar call itself — no roster/team fetch here,
 * unlike AnalyseTrade, since a suggestion card only needs the player names
 * the backend already resolved.
 *
 * "Analyse this trade →" used to mutate AnalyseTrade's sibling state and
 * flip an internal tab (both lived in one file). Now it's a real navigation:
 * it hands the target manager and the full give/get player lists to
 * /trade/analyse via router location state — see that page's prefill effect.
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { leaguesApi } from '../../services/api'
import AppShell from '../../components/AppShell'
import ToolHeader from '../../components/ToolHeader'
import { JAI_FONTS } from '../../components/JaiBrand'
import { V2, V2Card, V2Icons, JaiSummaryCard, PrimaryBtn, SecondaryBtn, V2Bar, solidChip, pctTone, RefreshButton } from '../../components/JaiV2'
import { JAIMascot } from '../../components/JAIMascot'
import { useDraftGate } from '../../hooks/useDraftGate'
import { GIVE_LABEL, GET_LABEL, VERDICT_META, DraftPendingState } from '../../components/trade/tradeShared'
import { timeAgo } from '../../utils/datetime'

const RADAR_TONES = {
  PROPOSE:      { color: V2.success },
  ALTER:        { color: V2.warning },
  DONT_PROPOSE: { color: V2.error },
}

const RADAR_EMPTY_COPY = {
  'draft-incomplete': "Trade Radar unlocks once your draft is complete.",
  'no-opponents': "JAI couldn't read any other manager's roster in this league yet.",
  'no-roster': "JAI couldn't read your roster right now.",
  'league-unreachable': "JAI couldn't reach your league's platform right now.",
  'generation-failed': "JAI couldn't put together trade suggestions this time.",
  error: "Something went wrong loading the Trade Radar.",
}

export default function TradeRadar() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { league, loading: leagueLoading, draftPending } = useDraftGate(id)

  const [radar, setRadar]              = useState(null)
  const [radarLoading, setRadarLoading] = useState(false)

  const loadRadar = useCallback(async (force = false) => {
    setRadarLoading(true)
    try {
      const res = await leaguesApi.getTradeRadar(id, { refresh: force })
      setRadar(res.data)
    } catch (err) {
      console.error('Trade Radar failed to load', err)
      setRadar({ available: false, reason: 'error' })
    } finally {
      setRadarLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (!leagueLoading && !draftPending && radar === null) {
      loadRadar(false)
    }
  }, [leagueLoading, draftPending, radar, loadRadar])

  const analyseSuggestion = (s) => {
    navigate(`/league/${id}/trade/analyse`, {
      state: {
        fromRadar: true,
        targetTeamId: s.targetTeamId,
        givingNames: s.youGive?.length ? [...s.youGive] : [],
        receivingNames: s.youGet?.length ? [...s.youGet] : [],
      },
    })
  }

  if (leagueLoading) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center h-full min-h-screen gap-3">
          <JAIMascot size={56} state="thinking" animate={true} />
          <p className="text-sm" style={{ color: V2.faint }}>Loading…</p>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <div style={{ padding: '30px 40px', fontFamily: JAI_FONTS.tight }}>
        <div style={{ maxWidth: 1040, margin: '0 auto' }}>
          <ToolHeader
            leagueName={league?.name}
            title="Trade Radar"
            subtitle="Trades worth pitching, ranked by how much they help you."
            back={{ label: 'Go to League Home Screen', to: `/league/${id}` }}
            backRight={{ label: 'Go to Trade Analyser', to: `/league/${id}/trade` }}
          />

          {draftPending ? (
            <DraftPendingState />
          ) : (
            <TradeRadarView radar={radar} loading={radarLoading} onAnalyse={analyseSuggestion} onRefresh={() => loadRadar(true)} />
          )}
        </div>
      </div>
    </AppShell>
  )
}

function RadarStatusLine({ radar, checkedCount, suggestionCount, onRefresh }) {
  const updated = timeAgo(radar?.generatedAt)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginTop: 20 }}>
      <p style={{ textAlign: 'center', fontSize: 11.5, color: V2.faint, margin: 0 }}>
        {checkedCount != null && (
          <>Checked {checkedCount} manager{checkedCount === 1 ? '' : 's'} · {suggestionCount} worth pitching · </>
        )}
        {updated ? `Updated ${updated}` : 'Radar refreshes weekly'}
      </p>
      <RefreshButton onClick={onRefresh} label="Refresh now" credits="tradeRadar" style={{ fontSize: 11.5, padding: '5px 12px' }} />
    </div>
  )
}

function TradeRadarView({ radar, loading, onAnalyse, onRefresh }) {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3" style={{ padding: '48px 0' }}>
        <JAIMascot size={48} state="thinking" animate={true} />
        <p style={{ fontSize: 13, color: V2.faint }}>Scanning every roster in your league…</p>
      </div>
    )
  }

  const notAvailable = !radar || radar.available === false
  const suggestions = radar?.suggestions || []
  const checkedCount = radar?.checkedCount

  if (notAvailable) {
    const reason = radar?.reason || 'error'
    return (
      <V2Card pad="28px 24px" style={{ textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
          <JAIMascot size={56} state="idle" animate={false} />
        </div>
        <p style={{ fontSize: 13, color: V2.mute, maxWidth: 420, margin: '0 auto 14px' }}>
          {RADAR_EMPTY_COPY[reason] || RADAR_EMPTY_COPY.error}
        </p>
        {reason !== 'draft-incomplete' && (
          <SecondaryBtn onClick={onRefresh} style={{ fontSize: 12 }}>Try again</SecondaryBtn>
        )}
      </V2Card>
    )
  }

  if (suggestions.length === 0) {
    return (
      <V2Card pad="28px 24px" style={{ textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
          <JAIMascot size={56} state="idle" animate={false} />
        </div>
        <p style={{ fontSize: 13, color: V2.mute, maxWidth: 420, margin: '0 auto 14px' }}>
          {checkedCount != null
            ? `JAI checked ${checkedCount} other manager${checkedCount === 1 ? '' : 's'} — none had a trade worth pitching right now.`
            : "No strong trades right now — JAI didn't find a swap worth pitching to another manager."}
        </p>
        <SecondaryBtn onClick={onRefresh} style={{ fontSize: 12 }}>Try again</SecondaryBtn>
      </V2Card>
    )
  }

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <JaiSummaryCard headline="Trades worth pitching, ranked by how much they help you.">
          I've scanned every roster in your league and matched your surpluses against their needs.
        </JaiSummaryCard>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {suggestions.map((s, i) => (
          <RadarCard key={`${s.targetTeamId}-${i}`} suggestion={s} onAnalyse={() => onAnalyse(s)} />
        ))}
      </div>

      <RadarStatusLine radar={radar} checkedCount={checkedCount} suggestionCount={suggestions.length} onRefresh={onRefresh} />
    </div>
  )
}

function RadarPlayerList({ label, names, color }) {
  return (
    <div style={{ flex: 1, minWidth: 140 }}>
      <div style={{ fontSize: 9.5, color: V2.faint, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 5, fontFamily: JAI_FONTS.mono }}>{label}</div>
      {names.map(n => (
        <div key={n} style={{ fontSize: 13.5, fontWeight: 700, color, marginBottom: 3 }}>{n}</div>
      ))}
    </div>
  )
}

function RadarCard({ suggestion: s, onAnalyse }) {
  const tone = RADAR_TONES[s.verdict] || RADAR_TONES.ALTER
  const verdictChip = solidChip(tone.color)
  const score = s.acceptanceScore ?? 0
  const youGive = s.youGive || []
  const youGet = s.youGet || []
  const surplusYou = s.surplusYou || []
  const needYou = s.needYou || []

  return (
    <V2Card pad="20px 22px">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: V2.navy }}>{s.targetManager}</div>
          <div style={{ fontSize: 11, color: V2.faint, letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: 1 }}>Trade target</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 110 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
              <span style={{ fontSize: 9.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: V2.faint, fontFamily: JAI_FONTS.mono }}>Score</span>
              <span style={{ fontSize: 15, fontWeight: 800, color: pctTone(score), fontFamily: JAI_FONTS.mono }}>{score}</span>
            </div>
            <V2Bar value={score} tone={score >= 80 ? 'strong' : 'neutral'} height={5} />
          </div>
          <span style={{ ...verdictChip, fontSize: 10, fontWeight: 800, padding: '4px 10px', borderRadius: 6, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>
            {VERDICT_META[s.verdict]?.label || s.verdict}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap', background: V2.paper, borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
        <RadarPlayerList label={GIVE_LABEL} names={youGive} color={V2.navy} />
        <span style={{ color: V2.blue, display: 'flex', flexShrink: 0, marginTop: 3 }}>{V2Icons.trade}</span>
        <RadarPlayerList label={GET_LABEL} names={youGet} color={V2.blue} />
      </div>

      {s.unfairTo && (
        <div style={{ fontSize: 12, color: V2.error, marginBottom: 12 }}>
          This trade is unfair to {s.unfairTo === 'me' ? 'you' : s.targetManager || 'them'}.
        </div>
      )}

      {(surplusYou.length > 0 || needYou.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
          <div style={{ ...solidChip(V2.success), borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.09em', textTransform: 'uppercase', marginBottom: 6, fontFamily: JAI_FONTS.mono, opacity: 0.85 }}>Fills their need</div>
            {surplusYou.map(c => (
              <div key={c} style={{ fontSize: 12, marginBottom: 2 }}>· {c}</div>
            ))}
          </div>
          <div style={{ ...solidChip(V2.error), borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.09em', textTransform: 'uppercase', marginBottom: 6, fontFamily: JAI_FONTS.mono, opacity: 0.85 }}>Fills your need</div>
            {needYou.map(c => (
              <div key={c} style={{ fontSize: 12, marginBottom: 2 }}>· {c}</div>
            ))}
          </div>
        </div>
      )}

      <p style={{ fontSize: 13, color: V2.mute, lineHeight: 1.6, margin: '0 0 14px' }}>
        {s.reasoning}
      </p>

      <PrimaryBtn onClick={onAnalyse} credits="tradeAnalyser">Analyse this trade →</PrimaryBtn>
    </V2Card>
  )
}
